import React, { useEffect, useMemo, useState ,useRef} from "react";
import Header from "../Header";
import PropertyList from "../PropertyList";
import ParcelFilters from "./ParcelFilters";
import ParcelMap from "./ParcelMap";
import ButtonFilter from "./ButtonFilter";

const parcelFile = "matched_fema_intersect.geojson";

const ParcelScreen = () => {
  // UI & app state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(localStorage.getItem("isSubscribed") === "true");
  const [parcelData, setParcelData] = useState([]); // normalized full set
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [showPoints, setShowPoints] = useState(true);

  // controlled filter states (passed down to ParcelFilters)
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [areaRange, setAreaRange] = useState([0, 5000]);
  const [zestimateRange, setZestimateRange] = useState([0, 1000000]);
  const [spreadRange, setSpreadRange] = useState([0, 100]);
  const [appreciationValue, setAppreciationValue] = useState("All");
  const [floodRiskFilter, setFloodRiskFilter] = useState("All");
  const [zoneCodeFilter, setZoneCodeFilter] = useState("All");

    // new listed sales filter (All | Listed | Unlisted)
  const [listedSalesFilter, setListedSalesFilter] = useState("All")
  

  // slider bounds derived from data
  const [sliderMinMax, setSliderMinMax] = useState({
    minPrice: 0, maxPrice: 1000000, minArea: 0, maxArea: 5000, minZestimate: 0, maxZestimate: 1000000, minSpread: 0, maxSpread: 100,
  });
    // ✅ ADD: Ref to access ParcelMap instance
  const parcelMapRef = useRef(null);
    // ✅ ADD: Handle row click function
  const handleRowClick = (property) => {
    if (!parcelMapRef.current) {
      console.warn("ParcelMap ref not available");
      return;
    }
    
    // Extract coordinates
    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);
    
    if (isNaN(lat) || isNaN(lng)) {
      console.warn("Invalid coordinates for property:", property.parcel || property.addrline1);
      return;
    }

    // Call ParcelMap's flyToLocation method
    if (typeof parcelMapRef.current.flyToLocation === 'function') {
      parcelMapRef.current.flyToLocation(lng, lat, property);
    }
  };

  useEffect(() => {
    // load geojson and normalize properties
    const load = async () => {
      try {
        const res = await fetch(`/assets/parcel/${parcelFile}`);
        const data = await res.json();
        const feats = (data.features || []).map((f) => {
          const p = { ...(f.properties || {}) };

          // normalize flood
          const flood = p._floodRisk ?? p.FloodFemaRisk ?? p.FloodRisk ?? p.fema_category ?? p.flood_risk ?? p.flood ?? "Unknown";
          p._floodRisk = String(flood);

          // normalize zone
          const zone = p._zone ?? p.ZonedCodeLocal ?? p.ZonedCode ?? p.ZoneCode ?? p.zoning ?? p.zoning_code ?? "Unknown";
          p._zone = String(zone);

          // normalize area (try several fields; support 'acre' conversion)
          // const areaCandidates = [p._area, p.parcelsize, p.AreaLotSF, p.AreaBuilding, p.area, p.area_sqft, p.lotsize, p.LotSizeSqFt];
              const areaCandidates = [p.arealotsf, p.AreaLotSF, p._area, p.parcelsize, p.AreaBuilding, p.area, p.area_sqft, p.lotsize, p.LotSizeSqFt];
          let areaVal = null;
          for (const v of areaCandidates) {
            if (v === undefined || v === null) continue;
            const s = String(v).trim();
            if (/acre/i.test(s)) {
              const num = parseFloat(s.replace(/[^\d.\-]/g, ""));
              if (!Number.isNaN(num)) { areaVal = Math.round(num * 43560); break; }
            }
            const n = parseFloat(String(v).replace(/[^\d.\-]/g, ""));
            if (!Number.isNaN(n)) { areaVal = n; break; }
          }
          p._area = Number.isFinite(areaVal) ? Number(areaVal) : 0;

          // coords -> numeric
          if (p.longitude !== undefined) p.longitude = Number(p.longitude);
          if (p.latitude !== undefined) p.latitude = Number(p.latitude);
          if (p.lon !== undefined && p.longitude === undefined) p.longitude = Number(p.lon);
          if (p.lat !== undefined && p.latitude === undefined) p.latitude = Number(p.lat);

          return { ...f, properties: p };
        });

        // sort (example: AreaBuilding descending)
        const sorted = feats.sort((a, b) => (parseFloat(b.properties?.AreaBuilding) || 0) - (parseFloat(a.properties?.AreaBuilding) || 0));

        setParcelData(sorted);
        setFilteredParcels(sorted);

        // compute slider bounds
        const toNum = (v) => {
          if (v === undefined || v === null) return NaN;
          const n = parseFloat(String(v).replace(/[^\d.\-]/g, ""));
          return Number.isFinite(n) ? n : NaN;
        };
        const prices = sorted.map((f) => toNum(f.properties?.LandValuePerSF ?? f.properties?.landvalueperunit ?? f.properties?.landvalue)).filter((v) => !Number.isNaN(v) && v > 0);
        // const areas = sorted.map((f) => toNum(f.properties?._area ?? f.properties?.AreaLotSF ?? f.properties?.parcelsize)).filter((v) => !Number.isNaN(v) && v > 0);
        const areas = sorted.map((f) => toNum(f.properties?.arealotsf ?? f.properties?.AreaLotSF ?? f.properties?._area ?? f.properties?.parcelsize)).filter((v) => !Number.isNaN(v) && v > 0);
        const zests = sorted.map((f) => toNum(f.properties?.zestimate)).filter((v) => !Number.isNaN(v) && v > 0);
        const spreads = sorted.map((f) => toNum(f.properties?.spreadpercentage ?? f.properties?.SpreadPercentage)).filter((v) => !Number.isNaN(v));

        setSliderMinMax({
          minPrice: prices.length ? Math.min(...prices) : 0,
          maxPrice: prices.length ? Math.max(...prices) : 1000000,
          minArea: areas.length ? Math.min(...areas) : 0,
          maxArea: areas.length ? Math.max(...areas) : 5000,
          minZestimate: zests.length ? Math.min(...zests) : 0,
          maxZestimate: zests.length ? Math.max(...zests) : 1000000,
          minSpread: spreads.length ? Math.min(...spreads) : 0,
          maxSpread: spreads.length ? Math.max(...spreads) : 100,
        });

        // initialize controlled ranges if defaults
        if (prices.length) setPriceRange([Math.min(...prices), Math.max(...prices)]);
        if (areas.length) setAreaRange([Math.min(...areas), Math.max(...areas)]);
        if (zests.length) setZestimateRange([Math.min(...zests), Math.max(...zests)]);
        if (spreads.length) setSpreadRange([Math.min(...spreads), Math.max(...spreads)]);
      } catch (err) {
        console.error("Parcel load error:", err);
      }
    };

    load();
  }, []);

  // Compute dropdown options (zones, floods) from normalized parcelData
  const zoneCodes = useMemo(() => {
    const s = new Set(parcelData.map((f) => f.properties?._zone ?? "Unknown"));
    return ["All", ...Array.from(s).sort()];
  }, [parcelData]);

  const floodOptions = useMemo(() => {
    const s = new Set(parcelData.map((f) => f.properties?._floodRisk ?? "Unknown"));
    return ["All", ...Array.from(s).sort()];
  }, [parcelData]);


      const getListedRaw = (props = {}) => {
    if (!props || typeof props !== "object") return null;
    const keysToTry = [
      "listedsale", "listed_sale", "listedSale", "ListedSale", "listingsale", "listedsales",
      "listing_status", "listingStatus", "status", "list_status", "listed_status",
      "is_listed", "isListed", "listed"
    ];
    for (const k of keysToTry) if (k in props && props[k] != null) return props[k];
    if (props.listing && typeof props.listing === "object") for (const k of keysToTry) if (k in props.listing && props.listing[k] != null) return props.listing[k];
    if (props.listingDetails && typeof props.listingDetails === "object") for (const k of keysToTry) if (k in props.listingDetails && props.listingDetails[k] != null) return props.listingDetails[k];
    for (const k of Object.keys(props)) if (/list|status/i.test(k) && props[k] != null) return props[k];
    return null;
  };

  // normalize to values used by the UI: "Listed", "Unlisted" or specific string
  const normalizeListed = (raw) => {
    if (raw === null || raw === undefined) return "Unlisted";
    if (typeof raw === "boolean") return raw ? "Listed" : "Unlisted";
    if (typeof raw === "number") return raw === 0 ? "Unlisted" : String(raw);
    const s = String(raw).trim();
    if (s === "") return "Unlisted";
    if (/^(true|yes|1|listed)$/i.test(s)) return "Listed";
    if (/^(false|no|0|unlisted|not listed)$/i.test(s)) return "Unlisted";
    return s.replace(/\s+/g, " ");
  };
  // Filtering: compute filteredParcels whenever any controlled filter changes
  useEffect(() => {
    const filtered = parcelData.filter((f) => {
      const p = f.properties || {};
      const toNum = (v) => {
        if (v === undefined || v === null) return 0;
        const n = parseFloat(String(v).replace(/[^\d.\-]/g, ""));
        return Number.isFinite(n) ? n : 0;
      };

      const price = toNum(p.LandValuePerSF ?? p.landvalueperunit ?? p.landvalue);
      const area = toNum(p._area ?? p.AreaLotSF ?? p.parcelsize);
      const zestimate = toNum(p.zestimate);
      const spread = toNum(p.spreadpercentage ?? p.SpreadPercentage ?? p.spread);
      const appreciation = toNum(p.Appreciation ?? p.appreciation);
      const flood = String(p._floodRisk ?? "Unknown").toLowerCase();
      const zone = String(p._zone ?? "Unknown").toLowerCase();

      // determine listed status (try several possible property names)
      const listedRaw = p.listed_sales ?? p.is_listed ?? p.ListedSales ?? p.listed ?? p.listed_status ?? null;
      const listingStatus = normalizeListed(getListedRaw(p));
      const isListed = Boolean(
        (typeof listedRaw === "number" && listedRaw > 0) ||
        (typeof listedRaw === "string" && /^(true|1|yes|listed)$/i.test(listedRaw)) ||
        listedRaw === true
      );
      

      const matchPrice = price >= priceRange[0] && price <= priceRange[1];
      const matchArea = area >= areaRange[0] && area <= areaRange[1];
      const matchZ = zestimate >= zestimateRange[0] && zestimate <= zestimateRange[1];
      const matchSpread = spread >= spreadRange[0] && spread <= spreadRange[1];
      const matchApp = appreciationValue === "All" || appreciation >= Number(appreciationValue);
      const matchFlood = floodRiskFilter === "All" || flood === String(floodRiskFilter).toLowerCase();
      const matchZone = zoneCodeFilter === "All" || zone === String(zoneCodeFilter).toLowerCase();

       // listed sales filter
      // const matchListed = listedSalesFilter === "All" || (listedSalesFilter === "Listed" && isListed) || (listedSalesFilter === "Unlisted" && !isListed);
 const matchListed = listedSalesFilter === "All" || String(listedSalesFilter) === String(listingStatus);
      return matchPrice && matchArea && matchZ && matchSpread && matchApp && matchFlood && matchZone && matchListed;
    });

    setFilteredParcels(filtered);
  }, [
    parcelData,
    priceRange,
    areaRange,
    zestimateRange,
    spreadRange,
    appreciationValue,
    floodRiskFilter,
    zoneCodeFilter,
    listedSalesFilter,
  ]);

  // Toggle view (points / polygons)
  const toggleView = (nextShowPoints) => setShowPoints(Boolean(nextShowPoints));

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <Header
          activeTab={"parcels"}
          handleTabChange={() => {}}
          toggleMenu={() => setIsMenuOpen((s) => !s)}
          isMenuOpen={isMenuOpen}
          handleLogout={() => { localStorage.removeItem("authToken"); window.location.href = "/"; }}
          geocoderContainerRef={null}
          showSearch={true}
          showFavorites={false}
          showLinks={true}
          isSubscribed={isSubscribed}
        />

        <div>
          <ParcelFilters
            parcelData={parcelData}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            areaRange={areaRange}
            setAreaRange={setAreaRange}
            zestimateRange={zestimateRange}
            setZestimateRange={setZestimateRange}
            spreadRange={spreadRange}
            setSpreadRange={setSpreadRange}
            appreciationValue={appreciationValue}
            setAppreciationValue={setAppreciationValue}
            floodRiskFilter={floodRiskFilter}
            setFloodRiskFilter={setFloodRiskFilter}
            zoneCodeFilter={zoneCodeFilter}
            setZoneCodeFilter={setZoneCodeFilter}
            sliderMinMax={sliderMinMax}
            zoneCodes={zoneCodes}
            floodOptions={floodOptions}
            onChange={setFilteredParcels}
            listedSalesFilter={listedSalesFilter}
            setListedSalesFilter={setListedSalesFilter}
          />
        </div>

        <div style={{ display: "flex", flex: 1, height: "calc(100vh - 150px)" }}>
          {/* <div style={{ width: "60%", borderRight: "1px solid #ddd", background: "##edf6f5", display: "flex", flexDirection: "column" }}> */}
            {/* <div style={{ padding: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <ButtonFilter label={showPoints ? "Points" : "Polygons"} active={showPoints} onClick={() => toggleView(!showPoints)} />
            </div> */}
            <div style={{ flex: 1,  }}>
              <PropertyList parcels={filteredParcels}
               onRowClick={handleRowClick} />
            </div>
          {/* </div> */}

          <div style={{ flex: 1 }}>
            <ParcelMap
              ref={parcelMapRef}
             parcels={filteredParcels} showPoints={showPoints}
             onFeatureClick={() => {}} 
             />
          </div>
        </div>
      </div>
    </>
  );
};

export default ParcelScreen;